/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import type { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { addParams } from 'lib/urlUtils';
import { IS_ANDROID_MOBILE, IS_FAMILY_USER, IS_IOS_MOBILE, IS_WEBVIEW, PROMO_TARIFFS } from 'reactApp/appHelpers/configHelpers';
import {
    isFamilyAfterPurchaseABEnabled,
    isFamilyAfterPurchaseCompact,
} from 'reactApp/appHelpers/featuresHelpers/features/familyAfterPurchase';
import { litresTariffIdsListCommon } from 'reactApp/appHelpers/featuresHelpers/features/litres';
import { vigboPaymentRedirect, vigboTariffs } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { openDisableAdsScreen } from 'reactApp/components/BuyDisableAds/BuyDisableAds';
import { openVkBuyModalWithSidebar } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.helpers';
import { EPaymentModalType } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.types';
import { checkMobileWebviewPayAvailable } from 'reactApp/modules/billing/billing.helpers';
import { updateBillingDataRequest } from 'reactApp/modules/billingData/billingData.module';
import { loadBillingDataIfNotLoaded } from 'reactApp/modules/billingData/billingData.provider';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { familyInitDataSuccess, familyLoadRequest } from 'reactApp/modules/family/family.actions';
import { isUserWithFamily, userCanCreateFamily } from 'reactApp/modules/family/family.selectors';
import { receivedGiftsUpdateSuccess } from 'reactApp/modules/giftReceived/actions/update.action';
import { updateReceivedGiftsActionFallback } from 'reactApp/modules/giftReceived/receivedGiftBillingData.adapter';
import {
    loadDataForSubscriptions,
    loadProductsAndOpenFastCheckout,
    openBuyCheckout,
    openBuyDisableAds,
    openExpiredSubsModal,
    openFastCheckout,
    openPromocodeGiftModalAfterBuy,
    openRenewSubscription,
    openTariffBuy,
} from 'reactApp/modules/payment/payment.module';
import type {
    LoadProductsAndOpenFastCheckoutAction,
    OpenBuyCheckoutAction,
    OpenBuyDisableAdsAction,
    OpenExpiredSubsModalAction,
    OpenFastCheckoutAction,
    OpenPromocodeGiftModalAfterBuyAction,
    OpenTariffBuyAction,
} from 'reactApp/modules/payment/payment.types';
import { openRenewModal } from 'reactApp/modules/payment/sagas/openRenewModal.saga';
import { authPopup } from 'reactApp/modules/ph/ph.thunkActions';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { productsController } from 'reactApp/modules/products/products.controller';
import { AUTOUPLOAD_10TB_REGEX, FLEXIBLE_QUOTA_TARIFFS_REGEX } from 'reactApp/modules/products/products.helpers';
import { updateProducts } from 'reactApp/modules/products/products.module';
import { getProductById, ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { EProductPeriod } from 'reactApp/modules/products/products.types';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { FLEXIBLE_QUOTA_SUCCESS_BUY } from 'reactApp/modules/promo/sagas/flexibleQuotaPromo';
import { initPromoTariffs } from 'reactApp/modules/promoTariffs/promoTariffs.saga';
import {
    GiftPurchasedSelectors,
    updatePurchasedGiftsActionFallback,
} from 'reactApp/modules/purchasedGifts/purchasedGiftsBillingData.adapter';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { updateUser } from 'reactApp/modules/user/user.thunkActions';
import { waitForUserUpdate } from 'reactApp/modules/user/waitForUserUpdate';
import { openAutouploadSuccessDialog } from 'reactApp/sections/QuotaLanding/AutouploadSuccessDialog/AutouploadSuccessDialog.helpers';
import { openLitresSuccessDialog } from 'reactApp/sections/QuotaLanding/LitresSuccessDialog/LitresSuccessDialog.helpers';
import { revealPlatformPopup } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import { openFamilyAfterPurchaseModal } from 'reactApp/ui/FamilyAfterPurchaseModal/FamilyAfterPurchaseModal.helpers';
import { openTariffBuyDialog } from 'reactApp/ui/TariffBuy/TariffBuy.helpers';
import { callSagaFromAction } from 'reactApp/utils/callSagaFromAction';
import opener from 'reactApp/utils/opener';
import { sendWebviewRadar } from 'reactApp/utils/sendWebviewRadar';
import { channel } from 'redux-saga';
import { call, cancel, put, putResolve, select, take, takeEvery } from 'redux-saga/effects';
import { race } from 'typed-redux-saga';

function* openFastCheckoutModal(payload: OpenFastCheckoutAction) {
    const {
        productId,
        onShow,
        onSuccess,
        afterUpdate,
        onClose,
        paySource,
        title,
        hideOtherTariffs,
        featuresList,
        hideSwitchSidebarBlock,
        onPayBtnClick,
        onNoTariff,
        enabledTrialMidasPayment,
    } = payload || {};
    const isPayAvailable = checkMobileWebviewPayAvailable();

    if (!isPayAvailable) {
        revealPlatformPopup(productId || '', IS_IOS_MOBILE, IS_ANDROID_MOBILE);
        sendWebviewRadar('fcheckout', 'open', isPayAvailable);
        return;
    }

    if (!productId) {
        yield call(initPromoTariffs, { noCancel: typeof onNoTariff === 'function' });
    }

    const tariffData = yield select(ProductsSelectors.getPrimaryProduct, productId);

    const { tariff, product } = tariffData || {};

    if (!product) {
        if (typeof onNoTariff === 'function') {
            onNoTariff();
        } else {
            yield put(
                showSnackbarAction({
                    type: SnackbarTypes.failure,
                    id: 'file-upload-err',
                    text: 'Нет доступных тарифов',
                    closable: true,
                })
            );
        }

        onClose?.();
        return;
    }

    const fastCheckoutChannel = channel();
    const isPhone = yield select(EnvironmentSelectors.isPhone);

    if (isPhone) {
        yield openTariffBuyDialog({
            onSuccess: () => fastCheckoutChannel.put(true),
            onClose: () => {
                onClose?.();
                fastCheckoutChannel.close();
            },
            onError: () => {
                onClose?.();
                fastCheckoutChannel.close();
            },
            id: product.id,
            isMobile: true,
            paySource,
            onPayBtnClick,
            title,
        });
    } else {
        yield openVkBuyModalWithSidebar({
            onSuccess: () => fastCheckoutChannel.put(true),
            onClose: () => {
                onClose?.();
                fastCheckoutChannel.close();
            },
            onShow,
            productId: product.id,
            tariff,
            type: EPaymentModalType.fastcheckout,
            paySource,
            featuresList,
            title,
            hideOtherTariffs,
            hideSwitchSidebarBlock,
            onPayBtnClick,
            enabledTrialMidasPayment,
        });
    }

    const isSuccess = yield take(fastCheckoutChannel);

    if (isSuccess) {
        yield onSuccess?.();
        yield put(updateProducts());
        yield waitForUserUpdate();
        yield afterUpdate?.();
    }
}

function* openPromocodeGiftModalAfterBuySaga(action: PayloadAction<OpenPromocodeGiftModalAfterBuyAction>) {
    const { productId } = action.payload;

    const product = yield select(ProductsSelectors.getProductById, productId);

    if (!product?.gift) {
        return;
    }

    try {
        yield putResolve(yield call(updatePurchasedGiftsActionFallback));
    } catch (_) {
        yield cancel();
    }

    const gift = yield select((state) => GiftPurchasedSelectors.getLastPurchasedGiftById(state, product.gift.id));

    openPopupHelper({
        popupName: popupNames.MOBILE_GIFT_PROMOCODE_DIALOG,
        data: {
            code: gift.code,
            space: gift.space,
            giftId: gift.id,
        },
    });
}

const openFastCheckoutModalFromAction = (action: PayloadAction<OpenFastCheckoutAction>) =>
    callSagaFromAction<OpenFastCheckoutAction>(openFastCheckoutModal, action);

function* openTariffBuyModal(action: PayloadAction<OpenTariffBuyAction>) {
    const {
        productId,
        onSuccess,
        onClose,
        source,
        checkAnonymous,
        isMobile,
        onError,
        isQuotaCleaner,
        paySource,
        onClick,
        subTitle,
        title,
        paymentDescription,
        enabledMidasPayment,
        enabledTrialMidasPayment,
        successMessage,
        hideOtherTariffs,
        hideSnackbars = false,
        onSkipPayment,
        showBackButton = false,
        onBackButtonClick = () => {},
        featuresList,
        vigboSkipRedirect,
    } = action.payload;

    const product = yield select(ProductsSelectors.getProductById, productId);

    const isAnonymous = yield select(UserSelectors.isAnonymous);

    if (checkAnonymous && isAnonymous) {
        yield put(
            authPopup({
                closable: true,
                loginRequest: true,
                successPage: addParams(window.location.href, {
                    action: 'request-payment',
                    productId,
                }),
            })
        );

        yield cancel();
    }

    const paymentChannel = channel();
    // tempexp_16481-next-line
    const closeChannel = channel();
    const errorChannel = channel();

    if (product?.hasTrial && !isMobile) {
        yield openVkBuyModalWithSidebar({
            showBackButton,
            onBackButtonClick,
            onSuccess: () => {
                paymentChannel.put(true);
            },
            onClose: () => {
                onClose?.();
                paymentChannel.close();
                // tempexp_17509-start
                closeChannel.put(true);
                // tempexp_17509-end
            },
            onError: () => {
                onError?.();
                errorChannel.put(true);
            },
            productId: product.id,
            type: EPaymentModalType.fastcheckout,
            paySource,
            title,
            hideSwitchSidebarBlock: true,
            hideOtherTariffs,
            featuresList,
            enabledTrialMidasPayment,
        });
    } else {
        yield openTariffBuyDialog({
            showBackButton,
            onBackButtonClick,
            source,
            isMobile,
            onSuccess: () => paymentChannel.put(true),
            onClose: () => {
                onClose?.();
                // tempexp_16481-next-line
                closeChannel.put(true);
                paymentChannel.close();
            },
            onError: () => {
                onError?.();
                errorChannel.put(true);
            },
            id: productId,
            isQuotaCleaner,
            paySource,
            onClick: () => {
                onClick?.();
            },
            subTitle,
            enabledMidasPayment,
            title,
            paymentDescription,
        });
    }

    const { isSuccess, isSkipPayment, isError } = yield* race({
        isSuccess: take(paymentChannel),
        isSkipPayment: take(closeChannel),
        isError: take(errorChannel),
    });

    // @todo: сделать проп коллбека закрытия окна после покупки
    if (isSuccess) {
        try {
            yield onSuccess?.();
        } catch (error) {
            logger.error(error);
        }

        // tempexp_16481-start
        if (AUTOUPLOAD_10TB_REGEX.test(product?.id)) {
            yield put(updateReceivedGiftsActionFallback());
        }

        // tempexp_18683-start
        if (FLEXIBLE_QUOTA_TARIFFS_REGEX.test(product?.id)) {
            storeHelper.setValue(FLEXIBLE_QUOTA_SUCCESS_BUY, true);
        }
        // tempexp_18683-end

        // tempexp_16481-end
        yield put(updateProducts());
        yield put(CardActions.updateCard({ isSilent: true, delay: 1000 }));
        yield put(updateBillingDataRequest({ radar: 'subs' }));
        // tempexp_16481-start
        if (!AUTOUPLOAD_10TB_REGEX.test(product?.id) && !isFamilyAfterPurchaseABEnabled) {
            yield put(updateUser());
        }
        // tempexp_16481-end

        if (!hideSnackbars) {
            yield put(
                showSnackbarAction({
                    type: SnackbarTypes.success,
                    id: 'buy',
                    text: successMessage || 'Подписка успешно оформлена',
                    closable: true,
                })
            );
        }

        // tempexp_16481-start
        if (AUTOUPLOAD_10TB_REGEX.test(product?.id)) {
            yield take(closeChannel);
            if (!IS_WEBVIEW) {
                yield openAutouploadSuccessDialog();
            }
            yield take(receivedGiftsUpdateSuccess);
            yield put(updateUser());
        }
        // tempexp_16481-end

        // tempexp_17509-start
        if (litresTariffIdsListCommon.includes(product?.id)) {
            yield take(closeChannel);
            yield openLitresSuccessDialog();
            yield put(updateUser());
        }
        // tempexp_17509-end

        // tempexp_18014-start
        if (isFamilyAfterPurchaseABEnabled) {
            yield putResolve(yield call(updateUser));
            yield take(closeChannel);
            const withFamily = yield select(isUserWithFamily);
            if (!IS_FAMILY_USER && !withFamily) {
                yield put(familyLoadRequest({}));
                yield take(familyInitDataSuccess);
                const canCreateFamilyByBilling = yield select(BillingDataSelectors.isUserCanCreateFamilyByBilling);
                const canCreateFamily = yield select(userCanCreateFamily);
                if (canCreateFamily && canCreateFamilyByBilling) {
                    yield openFamilyAfterPurchaseModal({
                        compact: isFamilyAfterPurchaseCompact,
                    });
                }
            }
        }
        // tempexp_18014-end

        // tempexp_19429-start
        if (vigboPaymentRedirect && vigboTariffs.includes(product?.id) && !vigboSkipRedirect) {
            yield take(closeChannel);
            const { url, currentPage } = vigboPaymentRedirect;
            opener(url, currentPage);
        }
        // tempexp_19429-end
    } else if (isSkipPayment && !isError) {
        // Вызываем действие когда пользователь закрыл окно не совершив оплату.
        onSkipPayment?.();
    }
}

function* loadDataForSubscriptionsSaga() {
    yield call(loadBillingDataIfNotLoaded);
    yield call(initPromoTariffs, {});
    yield put(CardActions.loadCard());
}

function* loadProductsAndOpenFastCheckoutSaga(action: PayloadAction<LoadProductsAndOpenFastCheckoutAction>) {
    const { quota, productId, onSuccess, paySource, title, hideOtherTariffs, enabledTrialMidasPayment } = action.payload;

    yield productsController.loadProducts();

    const product = yield select(getProductById, productId);
    const { product: productByQuota } = yield select(ProductsSelectors.getProductAndTariffByQuota, quota, EProductPeriod.year, false);

    yield openFastCheckoutModal({
        productId: productId ? product?.id : productByQuota?.id,
        onSuccess,
        paySource,
        title,
        hideOtherTariffs,
        enabledTrialMidasPayment,
    });
}

function* openBuyCheckoutModal(action: PayloadAction<OpenBuyCheckoutAction>) {
    yield productsController.loadProducts();

    const suggestedProductId = action.payload.productId || PROMO_TARIFFS?.default64GbTrial;
    const { product } = yield select(ProductsSelectors.getBuyFrameProduct, suggestedProductId);

    yield openFastCheckoutModal({
        ...action.payload,
        productId: product.id,
    });
}

function* openBuyDisableAdsModal(action: PayloadAction<OpenBuyDisableAdsAction>) {
    yield productsController.loadProducts();

    yield openDisableAdsScreen({
        ...action.payload,
    });
}

function* openExpiredSubsModaSagal(action: PayloadAction<OpenExpiredSubsModalAction>) {
    yield productsController.loadProducts();

    const { productId, onFailProduct } = action.payload;

    const product = yield select(ProductsSelectors.getProductById, productId);

    if (!product) {
        onFailProduct?.();
        yield cancel();
    }

    yield put(
        openTariffBuy({
            productId,
            paySource: 'mnz_recover_subs',
            checkAnonymous: false,
        })
    );
}

export function* watchPayment() {
    yield takeEvery(openTariffBuy.toString(), openTariffBuyModal);
    yield takeEvery(openFastCheckout.toString(), openFastCheckoutModalFromAction);
    yield takeEvery(openPromocodeGiftModalAfterBuy.toString(), openPromocodeGiftModalAfterBuySaga);
    yield takeEvery(loadDataForSubscriptions.toString(), loadDataForSubscriptionsSaga);
    yield takeEvery(loadProductsAndOpenFastCheckout.toString(), loadProductsAndOpenFastCheckoutSaga);
    yield takeEvery(openBuyCheckout.toString(), openBuyCheckoutModal);
    yield takeEvery(openBuyDisableAds.toString(), openBuyDisableAdsModal);
    yield takeEvery(openRenewSubscription.toString(), openRenewModal);
    yield takeEvery(openExpiredSubsModal.toString(), openExpiredSubsModaSagal);
}
